import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import stripePlatform from '../lib/stripe-platform';
import { stripeConfig } from '../lib/config';

const TermsOfService: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = (location.state as any)?.from || '/billing';

  const handleContinueToCheckout = async () => {
    if (!hasAgreed) {
      alert('You must agree to the Terms of Service to continue.');
      return;
    }

    try {
      setIsLoading(true);
      
      // Check if supabase is initialized
      if (!supabase) {
        console.error('Supabase client is not initialized');
        alert('Unable to connect to the database. Please try again later.');
        setIsLoading(false);
        return;
      }
      
      // Get current user's token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('You need to be logged in to subscribe');
        setIsLoading(false);
        return;
      }
      
      console.log('Creating checkout session with price:', stripeConfig.priceId);
      
      // Call the Stripe checkout function
      const response = await fetch(
        'https://idvgoctfaarxiryxprpy.supabase.co/functions/v1/stripe-create-checkout',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            priceId: stripeConfig.priceId,
            successUrl: `${window.location.origin}/billing/success`,
            cancelUrl: `${window.location.origin}/billing`
          })
        }
      );
      
      console.log('Edge Function response status:', response.status);
      
      // Try to parse the response as JSON
      let data;
      try {
        data = await response.json();
        console.log('Response data:', data);
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        const text = await response.text();
        console.log('Response text:', text);
        throw new Error(`Failed to parse response: ${text}`);
      }
      
      // Check for errors
      if (!response.ok) {
        throw new Error(data.error || `Error: ${response.status} ${response.statusText}`);
      }
      
      // Redirect to checkout
      if (data.url) {
        console.log('Redirecting to checkout URL:', data.url);
        
        // For a more professional approach, consider implementing one of these options:
        // 1. Use a URL shortener service (like Bitly) 
        // 2. Create a custom redirect page on your domain
        
        // Open in a new tab for better user experience
        window.open(data.url, '_blank');
      } else {
        throw new Error('No checkout URL returned from server');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      alert(`Failed to create checkout session: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-extrabold text-gray-900">Terms of Service</h1>
        <p className="mt-4 text-lg text-gray-600">
          Please read and agree to our Terms of Service before continuing
        </p>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="prose prose-blue max-w-none overflow-auto h-96 border border-gray-200 p-4 rounded-md mb-6">
            <h2>Effective Date: May 19, 2025</h2>
            <p>This Terms of Service and Software License Agreement ("Agreement") is a legal contract between you ("User," "you," or "your") and GitBooked, Inc. ("GitBooked," "we," "us," or "our"), governing your access to and use of the GitBooked software platform ("Software"). By subscribing to, accessing, or using the Software, you agree to be bound by this Agreement. If you do not agree to these terms, you may not access or use the Software.</p>
            
            <h3>1. Definitions</h3>
            <p>"Software" refers to the GitBooked web-based platform, including all features, functionalities, updates, and associated documentation, provided as a software-as-a-service (SaaS) solution.</p>
            <p>"License" refers to the non-exclusive, non-transferable, revocable right to access and use the Software as described in this Agreement.</p>
            <p>"Subscription" refers to the recurring payment plan for accessing the Software (Essential Plan), priced at $129.99 USD per month.</p>
            <p>"User Content" refers to any data, text, or materials uploaded or generated by you while using the Software.</p>
            <p>"Intellectual Property Rights" refers to all patents, copyrights, trademarks, trade secrets, and other proprietary rights related to the Software.</p>
            
            <h3>2. Grant of License</h3>
            <h4>2.1 Scope of License.</h4>
            <p>Subject to your compliance with this Agreement, GitBooked grants you a non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Software for your personal or internal business purposes during the term of your Subscription. This License does not confer ownership of the Software or any rights not expressly granted herein.</p>
            
            <h4>2.2 Restrictions.</h4>
            <p>You shall not:</p>
            <ul>
              <li>Copy, modify, distribute, or create derivative works of the Software.</li>
              <li>Reverse engineer, decompile, disassemble, or attempt to derive the source code of the Software, except as permitted by applicable law.</li>
              <li>Sell, lease, sublicense, or otherwise transfer the License to any third party.</li>
              <li>Use the Software to develop a competing product or service.</li>
              <li>Remove, alter, or obscure any copyright, trademark, or proprietary notices in the Software.</li>
            </ul>
            
            <h4>2.3 Reservation of Rights.</h4>
            <p>All rights not expressly granted in this Agreement are reserved by GitBooked. The Software and all Intellectual Property Rights therein remain the sole property of GitBooked.</p>
            
            <h3>3. Subscription and Payment</h3>
                         <h4>3.1 Subscription Fee.</h4>            <p>The Subscription fee for the Essential Plan is $129.99 USD per month, payable in advance. All fees are non-refundable except as expressly stated in this Agreement.</p>
            
            <h4>3.2 Payment Terms.</h4>
            <p>You authorize GitBooked or its third-party payment processor to charge your designated payment method automatically each month. You must provide valid, up-to-date payment information and ensure sufficient funds are available to cover the Subscription fee.</p>
            
            <h4>3.3 Taxes.</h4>
            <p>All fees exclude applicable taxes, duties, or similar charges, which are your responsibility unless otherwise stated.</p>
            
            <h4>3.4 Price Changes.</h4>
            <p>GitBooked may modify the Subscription fee upon thirty (30) days' prior written notice (via email or within the Software). Continued use of the Software after such notice constitutes acceptance of the new fee.</p>
            
            <h4>3.5 Late Payments.</h4>
            <p>If payment is not received by the due date, GitBooked may suspend or terminate your access to the Software without liability. You agree to reimburse GitBooked for any reasonable costs incurred in collecting overdue amounts, including attorney fees.</p>
            
            <h3>4. User Obligations</h3>
            <h4>4.1 Account Security.</h4>
            <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities conducted under your account. You agree to notify GitBooked immediately of any unauthorized access or use of your account.</p>
            
            <h4>4.2 Compliance with Laws.</h4>
            <p>You agree to use the Software in compliance with all applicable local, state, national, and international laws, including but not limited to data protection and privacy regulations.</p>
            
            <h4>4.3 Acceptable Use.</h4>
            <p>You shall not:</p>
            <ul>
              <li>Use the Software for any unlawful, harmful, or fraudulent purpose.</li>
              <li>Upload or transmit viruses, malware, or other harmful code.</li>
              <li>Interfere with or disrupt the Software's operation or servers.</li>
              <li>Attempt to gain unauthorized access to the Software or other users' accounts.</li>
            </ul>
            
            <h4>4.4 User Content.</h4>
            <p>You retain ownership of your User Content but grant GitBooked a worldwide, non-exclusive, royalty-free license to use, store, and process User Content solely to provide and improve the Software. You represent that you have all necessary rights to upload User Content and that it does not violate any third-party rights or applicable laws.</p>
            
            <h3>5. Intellectual Property</h3>
            <h4>5.1 Ownership.</h4>
            <p>The Software, including its code, design, documentation, and all updates, is owned by GitBooked and protected by copyright, trademark, and other Intellectual Property Rights. You acknowledge that you do not acquire any ownership interest in the Software under this Agreement.</p>
            
            <h4>5.2 Feedback.</h4>
            <p>If you provide feedback, suggestions, or improvements regarding the Software ("Feedback"), you grant GitBooked a perpetual, irrevocable, worldwide, royalty-free license to use, modify, and incorporate Feedback into the Software without compensation or attribution.</p>
            
            <h3>6. Confidentiality</h3>
            <p>You agree to treat as confidential any proprietary or non-public information disclosed by GitBooked in connection with the Software, including but not limited to technical specifications, pricing, or business plans ("Confidential Information"). You shall not disclose Confidential Information to third parties or use it for any purpose other than exercising your rights under this Agreement, except with GitBooked's prior written consent.</p>
            
            <h3>7. Data Privacy</h3>
            <h4>7.1 Data Processing.</h4>
            <p>GitBooked processes personal data in accordance with its Privacy Policy. By using the Software, you consent to such processing.</p>
            
            <h4>7.2 Data Security.</h4>
            <p>GitBooked implements reasonable technical and organizational measures to protect User Content and personal data. However, you acknowledge that no system is completely secure, and you assume the risk of unauthorized access or data breaches.</p>
            
            <h3>8. Term and Termination</h3>
            <h4>8.1 Term.</h4>
            <p>This Agreement begins upon your subscription to the Software and continues until terminated as provided herein.</p>
            
            <h4>8.2 Termination by User.</h4>
            <p>You may terminate your Subscription at any time by canceling through your account settings or contacting GitBooked support. No refunds will be provided for partial months.</p>
            
            <h4>8.3 Termination by GitBooked.</h4>
            <p>GitBooked may suspend or terminate your access to the Software immediately, without liability, if you:</p>
            <ul>
              <li>Breach any provision of this Agreement.</li>
              <li>Fail to pay the Subscription fee when due.</li>
              <li>Use the Software in a manner that violates applicable law or harms GitBooked or third parties.</li>
            </ul>
            
            <h4>8.4 Effect of Termination.</h4>
            <p>Upon termination, your License and access to the Software will cease. You must immediately stop using the Software, and GitBooked may delete your User Content after thirty (30) days, subject to applicable law. Sections 2.2, 2.3, 5, 6, 8.4, 9, 10, 11, and 12 survive termination.</p>
            
            <h3>9. Disclaimer of Warranties</h3>
            <p>THE SOFTWARE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. GITBOOKED DOES NOT WARRANT THAT THE SOFTWARE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS. YOU ASSUME ALL RISKS ASSOCIATED WITH THE USE OF THE SOFTWARE.</p>
            
            <h3>10. Limitation of Liability</h3>
            <h4>10.1 Exclusion of Damages.</h4>
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, GITBOOKED AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM OR RELATED TO THIS AGREEMENT OR THE SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.</p>
            
            <h4>10.2 Cap on Liability.</h4>
            <p>GITBOOKED'S TOTAL LIABILITY UNDER THIS AGREEMENT SHALL NOT EXCEED THE AMOUNT PAID BY YOU FOR THE SUBSCRIPTION IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.</p>
            
            <h4>10.3 Basis of Bargain.</h4>
            <p>These limitations apply regardless of the legal theory (contract, tort, or otherwise) and form an essential basis of this Agreement.</p>
            
            <h3>11. Indemnification</h3>
            <p>You agree to indemnify, defend, and hold harmless GitBooked and its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, or expenses (including reasonable attorney fees) arising from:</p>
            <ul>
              <li>Your use of the Software in violation of this Agreement.</li>
              <li>Your User Content or its violation of third-party rights.</li>
              <li>Your violation of applicable law.</li>
            </ul>
            
            <h3>12. Governing Law and Dispute Resolution</h3>
            <h4>12.1 Governing Law.</h4>
            <p>This Agreement is governed by the laws of the State of Delaware, USA, without regard to its conflict of laws principles.</p>
            
            <h4>12.2 Dispute Resolution.</h4>
            <p>Any dispute arising from this Agreement shall be resolved through binding arbitration in Delaware, USA, under the rules of the American Arbitration Association. The arbitrator's decision shall be final and enforceable in any court of competent jurisdiction. Notwithstanding the foregoing, GitBooked may seek injunctive relief in any court to protect its Intellectual Property Rights or Confidential Information.</p>
            
            <h4>12.3 Class Action Waiver.</h4>
            <p>You agree to resolve disputes on an individual basis and waive any right to participate in a class, collective, or representative action against GitBooked.</p>
            
            <h3>13. Miscellaneous</h3>
            <h4>13.1 Entire Agreement.</h4>
            <p>This Agreement constitutes the entire understanding between you and GitBooked regarding the Software and supersedes all prior agreements, whether written or oral.</p>
            
            <h4>13.2 Amendments.</h4>
            <p>GitBooked may modify this Agreement at any time by posting the updated terms on its website or within the Software. Continued use of the Software after such changes constitutes your acceptance of the revised Agreement.</p>
            
            <h4>13.3 Severability.</h4>
            <p>If any provision of this Agreement is found to be unenforceable, the remaining provisions shall remain in full force and effect.</p>
            
            <h4>13.4 Assignment.</h4>
            <p>You may not assign this Agreement or the License without GitBooked's prior written consent. GitBooked may assign this Agreement without restriction.</p>
            
            <h4>13.5 Force Majeure.</h4>
            <p>GitBooked shall not be liable for delays or failures in performance due to events beyond its reasonable control, including but not limited to natural disasters, cyberattacks, or government actions.</p>
            
            <h4>13.6 Notices.</h4>
            <p>Notices to GitBooked must be sent to Hello@gitbooked.com. Notices to you may be sent to the email address associated with your account.</p>
            
            <h4>13.7 No Waiver.</h4>
            <p>GitBooked's failure to enforce any provision of this Agreement does not constitute a waiver of that provision or any other right.</p>
          </div>
          
          <div className="flex items-center mb-6">
            <input
              id="terms-checkbox"
              type="checkbox"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              checked={hasAgreed}
              onChange={(e) => setHasAgreed(e.target.checked)}
            />
            <label htmlFor="terms-checkbox" className="ml-2 block text-sm text-gray-900">
              I have read and agree to the Terms of Service
            </label>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:space-x-4">
            <button
              type="button"
              onClick={handleContinueToCheckout}
              disabled={isLoading || !hasAgreed}
              className={`w-full sm:w-auto mb-3 sm:mb-0 inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                hasAgreed ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : 'Agree and Continue to Checkout'}
            </button>
            
            <button
              type="button"
              onClick={() => navigate(returnPath)}
              className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService; 